import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Loader2, Users, BarChart3, RefreshCw, Check, MessageSquare } from 'lucide-react';
import { generateIdentities, conductPoll } from '../api/api';
import { useToast } from '../hooks/use-toast';
import { applyAlpha } from '../lib/colorUtils';

const MarketResearchModal = ({ open, onClose, conversationId, content, suggestion, brandVisualTheme, initialData }) => {
  const [step, setStep] = useState('init'); // init, reviewing, polling, results
  const [segment, setSegment] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [manualSegment, setManualSegment] = useState('');
  const { toast } = useToast();

  // Derive brand colors
  const palette = brandVisualTheme?.palette || {};
  const primaryColor = palette.primary || '#4f46e5'; // Default indigo-600
  const primaryDark = palette.dark || '#312e81'; // Default indigo-900
  const primaryLight = applyAlpha(primaryColor, 0.1); // approx indigo-100
  const primaryLightest = applyAlpha(primaryColor, 0.05); // approx indigo-50
  const accentColor = palette.accent || '#DAFF35';
  
  // Initialize state
  useEffect(() => {
    if (open) {
      if (initialData) {
         setSegment({ label: initialData.segmentLabel, description: initialData.segmentDescription });
         setIdentities(initialData.identities);
         const resultsMap = {};
         initialData.identities.forEach(id => {
           if (id.result) resultsMap[id.id] = id.result;
         });
         setResults(resultsMap);
         setStep('results');
         setLoading(false);
      } else if (step === 'init') {
        initializeResearch();
      }
    }
  }, [open, initialData]);

  const initializeResearch = async () => {
    setLoading(true);
    try {
      // First pass: Infer segment and generate identities
      const data = await generateIdentities({
        conversationId,
        contentContext: content,
        count: 3
      });
      setSegment(data.segment);
      setIdentities(data.identities);
      setStep('reviewing');
    } catch (error) {
      console.error("Failed to init research", error);
      toast({ title: "Error", description: "Failed to generate audience.", variant: "destructive" });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!manualSegment.trim()) return;
    setLoading(true);
    try {
      const data = await generateIdentities({
        conversationId,
        segmentOverride: manualSegment,
        count: 3
      });
      setSegment(data.segment);
      setIdentities(data.identities);
      setManualSegment('');
    } catch (error) {
      console.error("Failed to regenerate", error);
      toast({ title: "Error", description: "Failed to update audience.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const runPoll = async () => {
    setStep('polling');
    const newResults = {};
    
    // Run polls in parallel
    await Promise.all(identities.map(async (identity) => {
      try {
        const result = await conductPoll({ content, identity });
        newResults[identity.id] = result.result;
      } catch (e) {
        console.error(`Poll failed for ${identity.name}`, e);
      }
    }));

    setResults(newResults);
    setStep('results');
  };

  const calculateOverallScore = () => {
    const scores = Object.values(results).map(r => r.score);
    if (!scores.length) return 0;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  const getScoreTheme = (score) => {
    if (score >= 80) {
      return {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-900',
        label: 'text-green-700'
      };
    }
    if (score >= 50) {
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-900',
        label: 'text-amber-700'
      };
    }
    return {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-900',
      label: 'text-red-700'
    };
  };

  const handleReset = () => {
    setStep('reviewing');
    setResults({});
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" style={{ color: primaryColor }} />
            Synthetic Market Research
          </DialogTitle>
          <DialogDescription>
            Test your content against a synthetic audience inferred from your brand and content.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          {loading && step === 'init' ? (
            <div className="h-full flex flex-col items-center justify-center space-y-4 min-h-[300px]">
              <Loader2 className="w-12 h-12 animate-spin" style={{ color: primaryColor }} />
              <p className="text-gray-500">Synthesizing market segment, identities, and generating portraits...</p>
            </div>
          ) : (
            <div className="space-y-8 py-4">
              {/* Segment Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    Target Audience
                  </h3>
                  {(step === 'reviewing' || step === 'results') && !initialData && (
                    <Button variant="outline" size="sm" onClick={() => setManualSegment(manualSegment ? '' : ' ')}>
                      Refine
                    </Button>
                  )}
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="font-medium" style={{ color: primaryDark }}>{segment?.label}</div>
                  <div className="text-sm text-gray-600 mt-1">{segment?.description}</div>
                  {segment?.demographics && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {segment.demographics.age_range && (
                        <Badge variant="secondary">{segment.demographics.age_range}</Badge>
                      )}
                      {segment.demographics.occupations?.slice(0,3).map(o => (
                        <Badge key={o} variant="outline">{o}</Badge>
                      ))}
                    </div>
                  )}
                </div>

                {manualSegment !== '' && (step === 'reviewing' || step === 'results') && !initialData && (
                  <div className="flex gap-2 animate-in fade-in slide-in-from-top-2">
                    <input
                      className="flex-1 px-3 py-2 border rounded-md text-sm"
                      placeholder="e.g., 'Tech-savvy grandmothers in Florida'"
                      value={manualSegment === ' ' ? '' : manualSegment}
                      onChange={(e) => setManualSegment(e.target.value)}
                    />
                    <Button 
                      size="sm" 
                      onClick={handleRegenerate}
                      disabled={loading || !manualSegment.trim()}
                      style={{ backgroundColor: primaryColor }}
                      className="text-white hover:opacity-90"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Update
                    </Button>
                  </div>
                )}
              </div>

              {/* Identities Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {identities.map((identity) => (
                  <div key={identity.id} className="border rounded-xl p-0 space-y-0 bg-white relative overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
                    {/* Header / Avatar */}
                    <div className="p-4 flex flex-col items-center text-center border-b bg-slate-50/50">
                      <div 
                        className="w-24 h-24 rounded-full mb-3 overflow-hidden ring-4 ring-white shadow-lg flex items-center justify-center font-bold text-2xl bg-cover bg-center"
                        style={{ 
                          backgroundColor: primaryLight, 
                          color: primaryColor,
                          backgroundImage: identity.avatar_url ? `url(${identity.avatar_url})` : undefined
                        }}
                      >
                        {!identity.avatar_url && identity.name[0]}
                      </div>
                      <div className="font-bold text-lg text-slate-900">{identity.name}, {identity.age}</div>
                      <div className="text-sm font-medium" style={{ color: primaryColor }}>{identity.occupation}</div>
                    </div>

                    <div className="p-4 space-y-4 flex-1">
                        {/* Bio */}
                        <p className="text-sm text-slate-600 italic leading-relaxed">
                        "{identity.bio}"
                        </p>

                        {/* Values */}
                        {identity.values && identity.values.length > 0 && (
                            <div className="space-y-1.5">
                                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Values</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {identity.values.map((v, i) => (
                                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                                            {v}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Pain Points */}
                        {identity.pain_points && identity.pain_points.length > 0 && (
                            <div className="space-y-1.5">
                                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Pain Points</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {identity.pain_points.map((p, i) => (
                                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-rose-50 text-rose-700 border border-rose-100">
                                            {p}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Result Overlay */}
                    {results[identity.id] && (
                      <div className="bg-slate-900/5 border-t p-4 animate-in fade-in">
                        <div className="flex justify-between items-center mb-3">
                          <Badge className={
                            results[identity.id].sentiment === 'positive' ? 'bg-green-600 hover:bg-green-700' : 
                            results[identity.id].sentiment === 'negative' ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-600 hover:bg-slate-700'
                          }>
                            {results[identity.id].sentiment}
                          </Badge>
                          <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold text-slate-900">{results[identity.id].score}</span>
                              <span className="text-xs text-slate-500">/100</span>
                          </div>
                        </div>
                        <div className="bg-white rounded p-3 text-sm text-slate-700 shadow-sm border italic relative">
                            <div className="absolute -top-2 left-4 w-4 h-4 bg-white border-t border-l transform rotate-45"></div>
                          "{results[identity.id].comments}"
                        </div>
                        {results[identity.id].suggestion && (
                          <div className="mt-3 pt-2 border-t border-slate-200/50">
                             <div className="flex items-center gap-1.5 text-xs font-medium mb-1" style={{ color: primaryColor }}>
                                <div className="w-1 h-1 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                                Suggestion
                             </div>
                             <p className="text-xs text-slate-600">{results[identity.id].suggestion}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {step === 'results' && (
                (() => {
                  const score = calculateOverallScore();
                  const theme = getScoreTheme(score);
                  return (
                    <div 
                      className={`border rounded-xl p-6 text-center ${theme.bg} ${theme.border}`}
                    >
                      <div className={`text-sm font-medium uppercase tracking-wide mb-1 ${theme.label}`}>Overall Score</div>
                      <div className={`text-4xl font-bold ${theme.text}`}>{score}</div>
                      <p className={`mt-2 max-w-md mx-auto ${theme.text} opacity-80`}>
                        Based on {identities.length} synthetic interviews.
                      </p>
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="pt-4 border-t">
          {step === 'reviewing' && (
             <Button 
              onClick={runPoll} 
              className="w-full md:w-auto text-white hover:opacity-90" 
              size="lg"
              style={{ backgroundColor: primaryColor }}
             >
               {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
               Run Research
             </Button>
          )}
          {step === 'polling' && (
            <Button disabled className="w-full md:w-auto">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Collecting Feedback...
            </Button>
          )}
          {step === 'results' && (
            <div className="flex gap-2 w-full justify-end">
               {!initialData && (
                 <Button variant="outline" onClick={handleReset}>Test Again</Button>
               )}
               <Button variant={initialData ? "default" : "outline"} onClick={onClose} style={initialData ? { backgroundColor: primaryColor } : undefined}>Close</Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarketResearchModal;
